import { getClasses } from '@/_utils/classnames';
import { Icon } from '@/_components';
import styles from './Avatar.module.css';
import { type FC, useMemo } from 'react';
import { getInitialsFromName } from '@/_utils/initials';
import type { AvatarProps } from './Avatar.types';

export const Avatar: FC<AvatarProps> = ({
  avatarUrl,
  className,
  size = 16,
  name,
  type = 'user',
}) => {
  const avatarClasses: string = getClasses(styles.avatar, className, {
    [styles.avatarMe]: type === 'me',
    [styles.avatarUser]: type === 'user',
    [styles.avatarFirm]: type === 'firm',
    [styles.avatarGroup]: type === 'group',
  });

  const getAvatar = (): React.ReactNode => {
    if (!!avatarUrl) {
      return (
        <img src={avatarUrl} alt="Profile" className={styles.avatar__img} />
      );
    }
    if (type === 'firm') return <Icon name="buildings" size={16} />;
    if (type === 'group') return <Icon name="users" size={16} />;

    const initials: string = getInitialsFromName(name);

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="50"
        height="50"
        className={styles.avatar__initialsSvg}
      >
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          preserveAspectRatio="xMidYMid meet"
          textAnchor="middle"
          fontSize="95%"
          fill="currentcolor"
        >
          {initials}
        </text>
      </svg>
    );
  };

  const style: object = useMemo(
    () => ({
      height: `${size}px`,
      maxHeight: `${size}px`,
      minHeight: `${size}px`,
      width: `${size}px`,
      maxWidth: `${size}px`,
      minWidth: `${size}px`,
    }),
    [size],
  );

  return (
    <div className={avatarClasses} style={style} data-component="Avatar">
      {getAvatar()}
    </div>
  );
};
